import type { NetworkAdapter } from '@splitfiesta/core';

/**
 * Web NetworkAdapter — `navigator.onLine` + `online`/`offline` window
 * events. Includes an optional `ping` that does a HEAD-equivalent GET
 * to a health endpoint so consumers can detect VPN / captive-portal
 * scenarios where `navigator.onLine` is true but real connectivity
 * is broken.
 */
export const webNetwork: NetworkAdapter = {
  isOnline() {
    try {
      return typeof navigator !== 'undefined' ? navigator.onLine : true;
    } catch {
      return true;
    }
  },

  subscribe(listener) {
    if (typeof window === 'undefined') return () => {};
    const onOnline = () => listener(true);
    const onOffline = () => listener(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  },

  async ping(url, timeoutMs) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  },
};
